import { defineStore } from "pinia";
import { ref } from "vue";
import { useLocalStorage } from "@vueuse/core";

export const useUIStore = defineStore("ui", () => {
  const sidebarCollapsed = useLocalStorage("aferi:ui:sidebarCollapsed", false);
  const theme = useLocalStorage<"light" | "dark" | "auto">("aferi:ui:theme", "auto");
  const commandOpen = useLocalStorage("aferi:ui:cmdOpen", false);
  // Drawer mobile (não persiste, sempre começa fechado)
  const mobileSidebarOpen = ref(false);
  // Título da entidade atual para o breadcrumb (ex.: nome do condomínio). A página
  // seta ao carregar e limpa ao sair; o header do Novo usa como última migalha.
  const crumbTitle = ref<string | null>(null);

  function toggleSidebar() { sidebarCollapsed.value = !sidebarCollapsed.value; }
  function toggleMobileSidebar() { mobileSidebarOpen.value = !mobileSidebarOpen.value; }
  function closeMobileSidebar() { mobileSidebarOpen.value = false; }
  function setTheme(t: "light" | "dark" | "auto") { theme.value = t; }
  function setCrumb(t: string | null) { crumbTitle.value = t; }

  return {
    sidebarCollapsed, theme, commandOpen, mobileSidebarOpen, crumbTitle,
    toggleSidebar, toggleMobileSidebar, closeMobileSidebar, setTheme, setCrumb,
  };
});

/* Toast bus simples (sem dep extra) */
export type ToastAcao = { label: string; onClick: () => void | Promise<void> };
type Toast = { id: string; msg: string; type?: "ok" | "err" | "info"; ttl?: number; acao?: ToastAcao };
const listeners = new Set<(t: Toast) => void>();
export function toast(msg: string, type?: Toast["type"], ttl = 2500) {
  const t: Toast = { id: Math.random().toString(36).slice(2), msg, type, ttl };
  listeners.forEach((l) => l(t));
}
/** Toast com um botão (ex.: "Desfazer"). Fica mais tempo na tela para dar tempo de clicar. */
export function toastAcao(msg: string, acao: ToastAcao, type: Toast["type"] = "ok", ttl = 9000) {
  const t: Toast = { id: Math.random().toString(36).slice(2), msg, type, ttl, acao };
  listeners.forEach((l) => l(t));
}
export function onToast(cb: (t: Toast) => void) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/* Modal de confirmação/alerta/prompt (substitui window.confirm/alert/prompt por UI estilizada) */
export type ConfirmOpts = {
  title?: string; message?: string; confirmText?: string; cancelText?: string;
  danger?: boolean; icon?: "warn" | "danger" | "info" | "ok";
  mode?: "confirm" | "alert" | "prompt"; placeholder?: string; defaultValue?: string;
};
export type ConfirmRequest = ConfirmOpts & { id: string; resolve: (v: any) => void };
const confirmListeners = new Set<(r: ConfirmRequest) => void>();
const rid = () => Math.random().toString(36).slice(2);

/** Confirmação estilizada. Retorna Promise<boolean>. */
export function askConfirm(opts: ConfirmOpts | string): Promise<boolean> {
  const o: ConfirmOpts = typeof opts === "string" ? { message: opts } : opts;
  return new Promise((resolve) => {
    const req: ConfirmRequest = { ...o, mode: "confirm", id: rid(), resolve };
    if (!confirmListeners.size) { resolve(window.confirm(o.message ?? "")); return; } // fallback
    confirmListeners.forEach((l) => l(req));
  });
}
/** Aviso estilizado (só OK). Retorna Promise<void>. */
export function askAlert(message: string, title?: string): Promise<void> {
  return new Promise((resolve) => {
    const req: ConfirmRequest = { message, title, mode: "alert", id: rid(), resolve: () => resolve() };
    if (!confirmListeners.size) { window.alert(message); resolve(); return; }
    confirmListeners.forEach((l) => l(req));
  });
}
/** Prompt estilizado. Retorna Promise<string|null>. */
export function askPrompt(opts: ConfirmOpts): Promise<string | null> {
  return new Promise((resolve) => {
    const req: ConfirmRequest = { ...opts, mode: "prompt", id: rid(), resolve };
    if (!confirmListeners.size) { resolve(window.prompt(opts.message ?? "") ?? null); return; }
    confirmListeners.forEach((l) => l(req));
  });
}
export function onConfirm(cb: (r: ConfirmRequest) => void) {
  confirmListeners.add(cb);
  return () => confirmListeners.delete(cb);
}
